import {
  FileIcon,
  MarkdownCard,
  Mtds,
  QrCodeIcon,
  TopBar,
  AppLayout,
  useGetOwnerNameQuery,
  useGetOkProgramsQuery,
  useGetPlanByIdQuery,
  useGetPurposesQuery,
  useGetSubPurposesQuery,
  useGetTaskByIdQuery,
  useGetLabReceptionAddressesQuery,
  SampleShippingAddress,
  useGetTaskDelegationQuery,
  useGetPlanSummariesQuery,
  DuedateTimeline,
  getSortedDuedates,
  getTaskDetailProperties,
  getTaskHeaderProperties,
  TaskScreenContent,
  PlanStatusTag,
  type Task
} from 'prove-common'
import { Link, useNavigate, useParams } from 'react-router'
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { TaskSamplesTaken } from '../components/task-samples-taken/TaskSamplesTaken'
import { formatPlanChain, getDetailsFromTask } from '../utils/taskHelper'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBar } from '../components/side-bar/SideBar'
import { TaskDelegationCard } from '../components/task-delegation-card/TaskDelegationCard'
import RegisterSampleModal from '../components/register-sample-modal/RegisterSampleModal'
import { TaskInstructions } from '../components/task-instructions/TaskInstructions'
import { TaskLegislationCard } from '../components/task-legislation-card/TaskLegislationCard'

type RegisterSampleState = {
  isDisabled: boolean
  text: string
}

function getRegisterSampleState(
  planStatus: Task['planStatus'],
  task: Task | undefined
): RegisterSampleState {
  if (planStatus !== 'active') {
    return {
      isDisabled: true,
      text: 'Prøver kan kun registreres på aktive oppgaver.'
    }
  }
  if (task?.analysisTypeApprovalStatus === 'awaiting') {
    return {
      isDisabled: true,
      text: 'Det er ikke mulig å registrere prøver på en oppgave som venter på godkjenning av analyse fra laboratorium.'
    }
  }
  if (task?.analysisTypeApprovalStatus === 'declined') {
    return {
      isDisabled: true,
      text: 'Det er ikke mulig å registrere prøver på en oppgave som har fått avvist analyse av laboratorium.'
    }
  }
  return {
    isDisabled: false,
    text: 'Registrer ny prøve på denne oppgaven'
  }
}

function getTaskStatusValue(task: Task): ReactNode {
  if (task.analysisTypeApprovalStatus === 'awaiting') {
    return <Mtds.Tag data-color='warning'>Avventer</Mtds.Tag>
  }

  if (task.analysisTypeApprovalStatus === 'declined') {
    return <Mtds.Tag data-color='danger'>Avvist</Mtds.Tag>
  }

  if (task.planStatus) {
    return <PlanStatusTag status={task.planStatus} />
  }

  return undefined
}

function getTaskAlert(task: Task): ReactNode {
  if (task.analysisTypeApprovalStatus === 'awaiting') {
    return (
      <Mtds.Alert data-color={'warning'}>
        <Mtds.Heading>
          Denne kombinasjonen avventer godkjenning fra laboratoriet
        </Mtds.Heading>
      </Mtds.Alert>
    )
  }

  if (task.analysisTypeApprovalStatus === 'declined') {
    return (
      <Mtds.Alert data-color={'danger'}>
        <Mtds.Heading>
          Denne kombinasjonen er avvist av laboratoriet
        </Mtds.Heading>
        <p>
          {task.approvalComment ??
            'Laboratoriet har avvist analysen for denne oppgaven.'}
        </p>
      </Mtds.Alert>
    )
  }

  return null
}

export const TaskScreen = () => {
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [expandedTaskId, setExpandedTaskId] = useState<string>()
  const [hasDescriptionOverflow, setHasDescriptionOverflow] = useState(false)
  const descriptionRef = useRef<HTMLDivElement>(null)
  const { taskId } = useParams()
  const isExpanded = expandedTaskId != null && expandedTaskId === taskId
  if (expandedTaskId != null && expandedTaskId !== taskId) {
    setExpandedTaskId(undefined)
  }
  const taskIdNumber = Number(taskId)
  const { data: plans } = useGetPlanSummariesQuery()
  const {
    data: task,
    isPending: isPendingTask,
    isError: isErrorTask,
    error: taskError
  } = useGetTaskByIdQuery(taskIdNumber)
  const receptionAddresses = useGetLabReceptionAddressesQuery(
    task?.labReceptionAddressId != null ? task.lab?.id : undefined
  )
  const receptionAddress = receptionAddresses.data?.find(
    (address) => address.id === task?.labReceptionAddressId
  )
  const {
    data: delegationResult,
    isPending: isPendingDelegation,
    isError: isErrorDelegation,
    error: delegationError
  } = useGetTaskDelegationQuery(taskIdNumber)
  const delegation = delegationResult?.delegation
  const details = task == null ? undefined : getDetailsFromTask(task, plans)
  const leafPlanId = task?.planIds?.[0]
  const { data: plan } = useGetPlanByIdQuery(leafPlanId)
  const { data: ownerName } = useGetOwnerNameQuery(details?.ownerId)
  const { data: purposes } = useGetPurposesQuery(task?.purposeId != null)
  const { data: subPurposes } = useGetSubPurposesQuery(
    task?.subPurposeId != null
  )
  const { data: okPrograms } = useGetOkProgramsQuery(plan?.okProgramId != null)
  const purpose = purposes?.find((r) => r.id === task?.purposeId)
  const subPurpose = subPurposes?.find((r) => r.id === task?.subPurposeId)
  const okProgram = okPrograms?.find((r) => r.id === plan?.okProgramId)
  const okProgramValue = okProgram ? (
    <>
      <strong>{okProgram.number}</strong> {okProgram.name}
    </>
  ) : undefined
  const planStatus = task?.planStatus
  const hasSubTasks = (delegation?.entries.length ?? 0) > 0
  const {
    isDisabled: isRegisterSampleButtonDisabled,
    text: registerSampleText
  } = getRegisterSampleState(planStatus, task)
  const subTaskList = (delegation?.entries ?? [])
    .filter((entry) => entry.path.length === 1)
    .map((entry) => ({
      entry,
      isCompleted: entry.samplesTaken >= entry.plannedQuantity
    }))
  useLayoutEffect(() => {
    const description = descriptionRef.current
    if (!description || isExpanded) return

    const measure = () =>
      setHasDescriptionOverflow(
        description.scrollHeight > description.clientHeight
      )
    measure()
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(measure)
    observer.observe(description)
    if (description.firstElementChild) {
      observer.observe(description.firstElementChild)
    }
    return () => observer.disconnect()
  }, [task?.description, isExpanded, isPendingTask, isPendingDelegation])

  const planChain = formatPlanChain(details?.planChain)
  const headerProperties = task
    ? getTaskHeaderProperties({
        task,
        statusValue: getTaskStatusValue(task),
        duedatesValue:
          getSortedDuedates(task.duedates).length > 0 ? (
            <DuedateTimeline duedates={task.duedates} />
          ) : (
            '-'
          )
      })
    : []
  const detailProperties = task
    ? getTaskDetailProperties({
        task,
        planValue: planChain,
        purposeValue: purpose?.name ?? '-',
        okProgramValue: okProgramValue ?? '-',
        subPurposeValue: subPurpose?.name ?? '-',
        ownerLabel: 'Tildelt',
        ownerValue: ownerName?.name ?? '-'
      })
    : []
  const breadcrumbItems = [
    {
      label: 'Oppgaver',
      to: '/tasks'
    },
    {
      label: task?.analysisType?.sampleType?.matrix,
      icon: <FileIcon />
    }
  ]

  const registrationUrl = `/samples/:code/registration?taskId=${taskId}`

  return (
    <AppLayout
      isError={isErrorTask || isErrorDelegation}
      error={taskError ?? delegationError}
      sidebar={<SideBar />}
      topBar={<TopBar logo={<AppLogo />} breadcrumbItems={breadcrumbItems} />}
      isLoading={isPendingTask || isPendingDelegation}
      isFullWidth
    >
      {task && (
        <TaskScreenContent
          title={task.analysisType?.sampleType?.matrix ?? 'Oppgave'}
          headerProperties={headerProperties}
          detailProperties={detailProperties}
          additionalDetails={
            leafPlanId != null ? (
              <TaskLegislationCard planId={leafPlanId} />
            ) : null
          }
          labDetails={
            task.labReceptionAddressId != null &&
            task.lab != null &&
            receptionAddresses.isPending ? (
              <Mtds.Muted role='status'>Laster mottaksadresse...</Mtds.Muted>
            ) : receptionAddresses.isError ? (
              <Mtds.Muted role='alert'>
                Kunne ikke laste mottaksadressen.
              </Mtds.Muted>
            ) : receptionAddress ? (
              <SampleShippingAddress
                address={receptionAddress}
                showName={false}
              />
            ) : null
          }
        >
          {getTaskAlert(task)}
          <MarkdownCard
            title='Beskrivelse'
            value={task.description ?? ''}
            emptyText='Ingen beskrivelse.'
            maxLength={2000}
            viewRef={descriptionRef}
            viewClassName={
              isExpanded
                ? undefined
                : hasDescriptionOverflow
                  ? 'description-preview description-preview-overflow'
                  : 'description-preview'
            }
          >
            {(hasDescriptionOverflow || isExpanded) && (
              <Mtds.Button
                data-variant='tertiary'
                type='button'
                aria-expanded={isExpanded}
                onClick={() =>
                  setExpandedTaskId(isExpanded ? undefined : taskId)
                }
                className='mx-auto'
              >
                {isExpanded ? 'Skjul' : 'Vis mer'}
              </Mtds.Button>
            )}
          </MarkdownCard>
          <Mtds.Card as={Mtds.Grid}>
            <Mtds.Muted>{registerSampleText}</Mtds.Muted>
            <div>
              {hasSubTasks ? (
                <Mtds.Button
                  disabled={isRegisterSampleButtonDisabled}
                  data-variant='primary'
                  onClick={() => setIsModalOpen(true)}
                >
                  <QrCodeIcon />
                  Registrer prøve
                </Mtds.Button>
              ) : (
                <Mtds.Button
                  aria-disabled={isRegisterSampleButtonDisabled}
                  as={Link}
                  data-variant='primary'
                  to={
                    isRegisterSampleButtonDisabled
                      ? '#'
                      : `/scan?type=sample&redirectUrl=${encodeURIComponent(registrationUrl)}`
                  }
                >
                  <QrCodeIcon />
                  Registrer prøve
                </Mtds.Button>
              )}
            </div>

            <RegisterSampleModal
              isOpen={isModalOpen}
              setOpen={setIsModalOpen}
              tasks={[task]}
              delegations={
                delegation == null
                  ? new Map()
                  : new Map([[task.id, delegation]])
              }
            />
          </Mtds.Card>
          {leafPlanId != null && <TaskInstructions planId={leafPlanId} />}
          {hasSubTasks && (
            <Mtds.Grid>
              <Mtds.Heading as='h2' data-size='sm'>
                Fordeling
              </Mtds.Heading>
              {subTaskList.map(({ entry, isCompleted }) => (
                <TaskDelegationCard
                  key={entry.taskDelegationId}
                  id={entry.taskDelegationId.toString()}
                  ownerId={entry.ownerId}
                  samplesTaken={entry.samplesTaken}
                  samplesToTake={entry.plannedQuantity}
                  countSubSamples={task.countSubSamples ?? false}
                  isCompleted={isCompleted}
                  onClick={(id: string) => {
                    navigate(`/tasks/${id}`)
                  }}
                />
              ))}
            </Mtds.Grid>
          )}
          {!hasSubTasks && (
            <TaskSamplesTaken
              taskIds={[taskIdNumber]}
              pageSize={
                details?.samplesTaken != null && details.samplesTaken > 20
                  ? details.samplesTaken
                  : 20
              }
            />
          )}
        </TaskScreenContent>
      )}
    </AppLayout>
  )
}
